// Home page - Landing para la aplicación de contratación

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fadeIn, slideUp, staggerContainer, staggerItem } from '@/utils/animations';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/providers/AuthProvider';

export function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [hiringCode, setHiringCode] = useState('');
  const { isAuthenticated } = useAuth();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hiringCode.trim()) {
      navigate(`/contratacion/${hiringCode}`);
    }
  };

  const features = [
    {
      title: t('home.features.verifyData'),
      description: t('home.features.verifyDataDesc'),
    },
    {
      title: t('home.features.kyc'),
      description: t('home.features.kycDesc'),
    },
    {
      title: t('home.features.payment'),
      description: t('home.features.paymentDesc'),
    },
    {
      title: t('home.features.contract'),
      description: t('home.features.contractDesc'),
    },
  ];

  return (
    <Layout>
      <motion.div 
        className="max-w-5xl mx-auto px-4 py-12"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        {/* Hero Section */}
        <motion.div 
          className="text-center mb-16"
          variants={slideUp}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('home.title')}
            <span className="block text-primary mt-2">{t('home.brand')}</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('home.subtitle')}
          </p>
        </motion.div>

        {/* Features */}
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          variants={staggerContainer}
        >
          {features.map((feature, index) => (
            <motion.div key={index} variants={staggerItem}>
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div variants={slideUp}>
          <Card className="bg-primary text-white">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">{t('home.cta.title')}</h2>
              <p className="mb-6 text-green-100">
                {t('home.cta.subtitle')}
              </p>
              <form onSubmit={handleSubmit} className="flex gap-4 justify-center">
                <input
                  type="text"
                  placeholder="AB12C"
                  maxLength={5}
                  value={hiringCode}
                  onChange={(e) => setHiringCode(e.target.value.toUpperCase())}
                  className="px-4 py-2 rounded-lg text-gray-900 uppercase text-center font-mono text-lg"
                  style={{ width: '150px' }}
                />
                <Button type="submit" variant="secondary" size="lg">
                  {t('home.cta.continue')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Acceso CRM (solo usuarios autenticados) */}
        {isAuthenticated && (
          <motion.div className="mt-8 text-center" variants={fadeIn}>
            <Button
              onClick={() => navigate('/crm/contacts')}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <MagnifyingGlassIcon className="w-5 h-5 mr-2" />
              {t('home.cta.searchContacts')}
            </Button>
          </motion.div>
        )}

        {/* Info */}
        <motion.div 
          className="mt-12 text-center text-sm text-gray-500"
          variants={fadeIn}
        >
          <p>{t('home.cta.noCode')}</p>
        </motion.div>
      </motion.div>
    </Layout>
  );
}
